// Whether a runtime's time zone rules are current, read from what they give, never from a version.
//
// Canada's change of 2026 (IANA tzdata 2026e): from 2026-11-01, British Columbia, Alberta, the Northwest
// Territories and Manitoba keep daylight time all year. A browser whose rules predate it shows their times an hour
// early — a patient's appointment at a location there among them. Each case is a zone and the offset the current rules give it on a winter Monday after the change; Toronto,
// which does not change, is the control. Chrome and Safari can carry 2026c and fail only Inuvik (2026d) and
// Winnipeg (2026e), so every case is asked, never Vancouver alone. The server checks the same table
// (blhWebServer src/config/timeZoneRules.js), and the clinic app too (blhClinicApp src/features/time-zone-rules).

/**
 * When the change is in force: midnight of 2026-11-01 in the first of the zones to reach it (Winnipeg, then on
 * daylight time, UTC−5). Before it, the old and the new rules read the present alike.
 */
export const CHANGE_IN_FORCE_AT = Date.parse("2026-11-01T05:00:00Z");

/** The instant every case is read at. */
export const RULES_CHECKED_AT = Date.parse("2026-11-16T18:00:00Z");

/** Each zone, and its offset from UTC in minutes at `RULES_CHECKED_AT` under the current rules. */
export const RULE_CASES: readonly {
  readonly zone: string;
  readonly offsetMinutes: number;
}[] = [
  {zone: "America/Vancouver", offsetMinutes: -7 * 60},
  {zone: "America/Edmonton", offsetMinutes: -6 * 60},
  {zone: "America/Inuvik", offsetMinutes: -6 * 60},
  {zone: "America/Winnipeg", offsetMinutes: -5 * 60},
  {zone: "America/Toronto", offsetMinutes: -5 * 60},
];

/** `Intl.DateTimeFormat`, the browser's or a polyfill's. */
export type DateTimeFormatConstructor = typeof Intl.DateTimeFormat;

/**
 * A zone name's offset ("GMT-07:00" as browsers write it, "GMT-7" as the polyfill does, "GMT+05:30", "GMT") as
 * minutes east of UTC; null for anything else.
 */
export function offsetMinutesOf(text: string): number | null {
  const match = /^(?:GMT|UTC)(?:([+−-])(\d{1,2})(?::?(\d{2}))?)?$/.exec(
    text.trim()
  );
  if (!match) return null;
  if (!match[1]) return 0;
  const minutes = Number(match[2]) * 60 + Number(match[3] ?? 0);
  return match[1] === "+" ? minutes : -minutes;
}

/** A zone's offset at an instant as an `Intl` reads it; null where it cannot. */
export function zoneOffsetMinutes(
  DateTimeFormat: DateTimeFormatConstructor,
  zone: string,
  at: number
): number | null {
  try {
    const name = new DateTimeFormat("en-US", {
      timeZone: zone,
      timeZoneName: "longOffset",
    })
      .formatToParts(at)
      .find((part) => part.type === "timeZoneName")?.value;
    return name === undefined ? null : offsetMinutesOf(name);
  } catch {
    return null;
  }
}

/** The cases an `Intl` reads wrongly: none when its rules are current. */
export function staleZones(
  DateTimeFormat: DateTimeFormatConstructor
): string[] {
  return RULE_CASES.filter(
    (each) =>
      zoneOffsetMinutes(DateTimeFormat, each.zone, RULES_CHECKED_AT) !==
      each.offsetMinutes
  ).map((each) => each.zone);
}

// The change's first winter, the spring and summer after it, and the next winter.
const COMPARED_AT = [
  "2026-11-16T18:00:00Z",
  "2027-01-15T18:00:00Z",
  "2027-03-20T18:00:00Z",
  "2027-07-15T18:00:00Z",
  "2027-11-15T18:00:00Z",
].map(Date.parse);

/**
 * Whether two `Intl`s read a zone differently: the browser's own rules against the current ones, for the zone of
 * the computer's own clock (a `Date`'s local methods follow the browser's own rules, which nothing replaces).
 */
export function readingsDiffer(
  a: DateTimeFormatConstructor,
  b: DateTimeFormatConstructor,
  zone: string
): boolean {
  return COMPARED_AT.some(
    (at) => zoneOffsetMinutes(a, zone, at) !== zoneOffsetMinutes(b, zone, at)
  );
}

/** What the browser's time zone rules came to before the app drew a time. */
export interface TimeZoneRules {
  /**
   * "current": the browser's own rules are current. "polyfilled": they were not, and the bundled rules replace
   * them. "stale": they were not, and the bundled rules could not be had: times in the zones that changed would be
   * an hour off.
   */
  readonly state: "current" | "polyfilled" | "stale";
  /** The cases the browser's own rules read wrongly (model/rules.ts). */
  readonly staleCases: readonly string[];
  /**
   * The computer's own clock zone, when the browser's own rules read it differently from the current ones: a
   * `Date`'s local methods follow them, and nothing replaces those. Null otherwise.
   */
  readonly localZoneStale: string | null;
}
